import * as XLSX from 'xlsx';
import {
  DataSyncExcelSheetSchema,
  type ListDataSyncExcelRowsResponse,
  type DataSyncExcelDownloadedIds
} from '@bliks-io/contracts';
import i18next from '@/lib/i18n';
import {
  getTranslatedHeaders,
  getColumnWidths,
  getFieldNames,
  getSheetName,
  DATA_SYNC_SHEET_AREAS,
  DATA_SYNC_DOWNLOADED_IDS_SHEET,
  type DataAreaType
} from './excelConfig';

/** A data row by field name; a missing or null field leaves its cell empty. */
export type FieldRow = Partial<Record<string, string | null>>;

/**
 * Append a data sheet: the translated header row, then `rows` in the data
 * area's column order. Templates ship no rows — the wizard explains every
 * column in the app itself, and example rows proved confusing (and get parsed
 * as real data on re-import).
 */
function appendDataSheet(
  workbook: XLSX.WorkBook,
  dataArea: DataAreaType,
  rows: FieldRow[] = []
): void {
  const fields = getFieldNames(dataArea);
  const dataSheet = XLSX.utils.aoa_to_sheet([
    getTranslatedHeaders(dataArea),
    ...rows.map((row) => fields.map((field) => row[field] ?? ''))
  ]);
  dataSheet['!cols'] = getColumnWidths(dataArea);
  XLSX.utils.book_append_sheet(workbook, dataSheet, getSheetName(dataArea));
}

/** A workbook of the data sheets given, in order. */
function buildWorkbook(sheets: { dataArea: DataAreaType; rows?: FieldRow[] }[]): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new();

  sheets.forEach(({ dataArea, rows }) => {
    appendDataSheet(workbook, dataArea, rows);
  });
  return workbook;
}

function writeWorkbook(workbook: XLSX.WorkBook, baseFilename: string): void {
  // Download with language suffix for clarity
  const lang = i18next.language;
  XLSX.writeFile(workbook, `${baseFilename}_${lang}.xlsx`);
}

/** Append the hidden sheet of the ids a data source download lists, as text cells. */
function appendDownloadedIdsSheet(
  workbook: XLSX.WorkBook,
  downloaded: DataSyncExcelDownloadedIds
): void {
  const keys = DataSyncExcelSheetSchema.options;
  const columns = keys.map((sheet) => downloaded[sheet]);
  const height = Math.max(...columns.map((ids) => ids.length));
  const rows = Array.from({ length: height }, (_, index) => columns.map((ids) => ids[index]));
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.aoa_to_sheet([keys, ...rows]),
    DATA_SYNC_DOWNLOADED_IDS_SHEET
  );
  XLSX.utils.book_set_sheet_visibility(workbook, DATA_SYNC_DOWNLOADED_IDS_SHEET, 1);
}

/** Generic template download: the main data sheet plus secondary sheets, all header-only */
function downloadTemplate(
  dataArea: DataAreaType,
  baseFilename: string,
  secondaryDataAreas: DataAreaType[] = []
): void {
  writeWorkbook(
    buildWorkbook([dataArea, ...secondaryDataAreas].map((sheet) => ({ dataArea: sheet }))),
    baseFilename
  );
}

/**
 * Generate and download Excel template for departments bulk upload
 */
export function downloadDepartmentsTemplate(): void {
  downloadTemplate('departments', 'departments_template');
}

/**
 * Generate and download Excel template for users bulk upload
 */
export function downloadUsersTemplate(): void {
  downloadTemplate('users', 'users_template');
}

/**
 * Generate and download Excel template for processes bulk upload
 */
export function downloadProcessesTemplate(): void {
  downloadTemplate('processes', 'processes_template');
}

/**
 * Generate and download Excel template for functions bulk upload
 * (main data sheet + memberships sheet)
 */
export function downloadFunctionsTemplate(): void {
  downloadTemplate('functions', 'functions_template', ['memberships']);
}

/**
 * Generate and download Excel template for teams bulk upload
 * (main data sheet + memberships sheet)
 */
export function downloadTeamsTemplate(): void {
  downloadTemplate('teams', 'teams_template', ['memberships']);
}

/**
 * Generate and download Excel template for a partner rep's contact list
 */
export function downloadPartnerContactsTemplate(): void {
  downloadTemplate('partnerContacts', 'contacts_template');
}

/**
 * Generate and download Excel template for systems bulk upload
 */
export function downloadSystemsTemplate(): void {
  downloadTemplate('systems', 'systems_template');
}

/**
 * Download the Excel data source: one sheet per list, one row per entity,
 * and last the hidden sheet of the ids the download lists.
 */
export function downloadDataSyncWorkbook(rows: ListDataSyncExcelRowsResponse): void {
  const workbook = buildWorkbook(
    DataSyncExcelSheetSchema.options.map((sheet) => ({
      dataArea: DATA_SYNC_SHEET_AREAS[sheet],
      rows: rows[sheet]
    }))
  );
  appendDownloadedIdsSheet(workbook, rows.downloaded);
  writeWorkbook(workbook, 'data_sync');
}
